import React, { useState } from 'react';
import './RigControlPanel.css';

const BACKEND_LABELS = {
  wsjtx: 'WSJT-X',
  flrig: 'FLRig',
  fldigi: 'fldigi',
  hamlib: 'Hamlib'
};

function formatFrequency(hz) {
  if (!hz) {
    return '—';
  }
  const mhz = Number(hz) / 1_000_000;
  if (!Number.isFinite(mhz)) {
    return '—';
  }
  return `${mhz.toFixed(3)} MHz`;
}

function RigControlPanel({
  operatorCallsign,
  settings,
  connectionState,
  backends,
  rigState,
  lastDecode,
  error,
  onSaveSettings,
  onReconnect
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(null);

  if (!operatorCallsign) {
    return null;
  }

  const activeDraft = draft || settings;
  const connectedBackend = rigState?.backend;
  const statusClass = connectionState === 'connected' ? 'rig-status-connected' : 'rig-status-disconnected';

  const updateDraft = (patch) => {
    setDraft({ ...activeDraft, ...patch });
  };

  const updateBackend = (name, patch) => {
    setDraft({
      ...activeDraft,
      backends: {
        ...activeDraft.backends,
        [name]: {
          ...activeDraft.backends[name],
          ...patch
        }
      }
    });
  };

  const handleSave = () => {
    onSaveSettings(activeDraft);
    setDraft(null);
  };

  return (
    <div className="rig-control-panel">
      <div className="rig-control-summary">
        <div className="rig-control-title">
          <span className={`rig-status-dot ${statusClass}`} />
          <strong>Rig Bridge</strong>
          <span className="rig-connection-label">{connectionState}</span>
        </div>
        <div className="rig-control-readout">
          {rigState ? (
            <>
              <span>{formatFrequency(rigState.frequencyHz)}</span>
              <span>{rigState.frequencyLabel || '—'}</span>
              <span>{rigState.mode || '—'}</span>
              {connectedBackend && (
                <span className="rig-backend-badge">{BACKEND_LABELS[connectedBackend] || connectedBackend}</span>
              )}
            </>
          ) : (
            <span className="rig-control-placeholder">No rig data — start Rig Bridge on this computer</span>
          )}
        </div>
        <div className="rig-control-actions">
          <label className="rig-toggle">
            <input
              type="checkbox"
              checked={activeDraft.followRig}
              onChange={(e) => {
                const next = { ...activeDraft, followRig: e.target.checked };
                setDraft(next);
                onSaveSettings(next);
              }}
            />
            Follow rig
          </label>
          <button type="button" className="btn-rig-secondary" onClick={onReconnect}>
            Reconnect
          </button>
          <button
            type="button"
            className="btn-rig-secondary"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Hide' : 'Settings'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rig-control-error">{error}</div>
      )}

      {lastDecode?.message && (
        <div className="rig-decode-banner">
          WSJT-X decode: <strong>{lastDecode.message}</strong>
          {Number.isFinite(lastDecode.snr) && <span> ({lastDecode.snr} dB)</span>}
        </div>
      )}

      {expanded && (
        <div className="rig-control-settings">
          <div className="rig-settings-row">
            <label htmlFor="rig-bridge-url">Bridge URL</label>
            <input
              id="rig-bridge-url"
              type="text"
              value={activeDraft.bridgeUrl}
              onChange={(e) => updateDraft({ bridgeUrl: e.target.value })}
            />
          </div>

          <div className="rig-settings-row">
            <label htmlFor="rig-primary-backend">Primary backend</label>
            <select
              id="rig-primary-backend"
              value={activeDraft.primaryBackend}
              onChange={(e) => updateDraft({ primaryBackend: e.target.value })}
            >
              <option value="auto">Auto-detect</option>
              <option value="wsjtx">WSJT-X</option>
              <option value="flrig">FLRig</option>
              <option value="fldigi">fldigi</option>
              <option value="hamlib">Hamlib</option>
            </select>
          </div>

          <label className="rig-toggle">
            <input
              type="checkbox"
              checked={activeDraft.wsjtxAutoLog}
              onChange={(e) => updateDraft({ wsjtxAutoLog: e.target.checked })}
            />
            WSJT-X auto-log (skip confirm dialog)
          </label>

          <div className="rig-backend-grid">
            {Object.entries(activeDraft.backends).map(([name, cfg]) => (
              <div key={name} className="rig-backend-card">
                <div className="rig-backend-card-header">
                  <label className="rig-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(cfg.enabled)}
                      onChange={(e) => updateBackend(name, { enabled: e.target.checked })}
                    />
                    {BACKEND_LABELS[name]}
                  </label>
                  <span className={`rig-backend-state ${backends[name]?.connected ? 'online' : 'offline'}`}>
                    {backends[name]?.connected ? 'online' : 'offline'}
                  </span>
                </div>
                <p className="rig-backend-detail">{backends[name]?.detail || 'Not checked yet'}</p>
                {name === 'wsjtx' && (
                  <label>
                    UDP port
                    <input
                      type="number"
                      value={cfg.udpPort}
                      onChange={(e) => updateBackend(name, { udpPort: Number(e.target.value) })}
                    />
                  </label>
                )}
                {(name === 'flrig' || name === 'fldigi') && (
                  <>
                    <label>
                      Host
                      <input
                        type="text"
                        value={cfg.host}
                        onChange={(e) => updateBackend(name, { host: e.target.value })}
                      />
                    </label>
                    <label>
                      Port
                      <input
                        type="number"
                        value={cfg.port}
                        onChange={(e) => updateBackend(name, { port: Number(e.target.value) })}
                      />
                    </label>
                  </>
                )}
                {name === 'hamlib' && (
                  <>
                    <label>
                      Model
                      <input
                        type="text"
                        value={cfg.rigModel}
                        onChange={(e) => updateBackend(name, { rigModel: e.target.value })}
                      />
                    </label>
                    <label>
                      Port
                      <input
                        type="text"
                        value={cfg.rigPort}
                        onChange={(e) => updateBackend(name, { rigPort: e.target.value })}
                      />
                    </label>
                    <label>
                      Baud
                      <input
                        type="text"
                        value={cfg.rigBaud}
                        onChange={(e) => updateBackend(name, { rigBaud: e.target.value })}
                      />
                    </label>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="rig-settings-actions">
            <button type="button" className="btn-rig-primary" onClick={handleSave}>
              Save rig settings
            </button>
            {draft && (
              <button type="button" className="btn-rig-secondary" onClick={() => setDraft(null)}>
                Reset
              </button>
            )}
          </div>

          <p className="rig-help-text">
            <strong>Rig Bridge</strong> is optional — only for **your** radio setup on this device.
            Desktop: <code>npm start</code> in <code>rig-bridge/</code>.
            iPad app: built-in bridge in Settings (your FLRig/WSJT-X, not a club shack PC).
          </p>
        </div>
      )}
    </div>
  );
}

export default RigControlPanel;
